/**
 * ada-shopchooka.js  --  Chooka (shopchooka.com) first-party accessibility script
 * Theme: Symmetry 8.3.2 (Shopify). Loaded from theme.liquid via asset_url.
 * Goal: WCAG 2.1 AA fixes that the theme does not do natively.
 * Layout: (1) main block, (2) VerifyPass iframe title, (3) product-grid alt text,
 *         (4) cookie banner label, (5) form success headings, (6) minor fixes (Oct 2026).
 * Note: theme.liquid also holds a11y code (skip link, focus trap, Filter toggle semantics).
 *       Keep both in mind before changing either.
 */

/* ===== (1) MAIN BLOCK ===== */
((function () {
  "use strict";
  /* Selector for anything a keyboard user can tab to. Used by initAriaHiddenFix. */
  var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
  /* Run every fix once the DOM is ready. Add new init functions to this list. */
  document.addEventListener("DOMContentLoaded", function () {
    (initGalleryA11y(),
      initAriaHiddenFix(),
      initMissingH1Fix(),
      initHomepageH1Fix(),
      initIconTitleFix(),
      initFocusFix(),
      initProductPageFix(),
      initLogoLinkFix(),
      initBlogPlaceholderFix());
  });
  /* Product gallery thumbnails -> accessible tabs (WCAG 4.1.2, 2.1.1).
   * Finds each thumbnail strip and hands it to setupScroller(). */
  function initGalleryA11y() {
    var scrollers = document.querySelectorAll(".product__thumbs--scroller");
    scrollers.length &&
      scrollers.forEach(function (scroller) {
        setupScroller(scroller);
      });
  }
  /* Gives one thumbnail strip tablist semantics: role=tablist/tab, aria-label "Image N of M",
   * aria-selected + roving tabindex, arrow/Home/End key navigation, and a live-region
   * announcement when the active image changes. Decorative thumb <img> alt is blanked
   * so the label isn't read twice. */
  function setupScroller(scroller) {
    (scroller.setAttribute("role", "tablist"),
      scroller.setAttribute("aria-label", "Product image thumbnails"));
    var thumbLinks = scroller.querySelectorAll("[data-product-thumb]"),
      total = thumbLinks.length,
      firstImg = thumbLinks[0] && thumbLinks[0].querySelector("img"),
      productName =
        (firstImg && firstImg.alt) ||
        (document.querySelector("h1") && document.querySelector("h1").textContent.trim()) ||
        "product image";
    thumbLinks.forEach(function (link, i) {
      (link.setAttribute("role", "tab"),
        link.setAttribute("aria-label", "Image " + (i + 1) + " of " + total + ": " + productName));
      var isActive = link.classList.contains("is-active");
      (link.setAttribute("aria-selected", isActive ? "true" : "false"),
        link.setAttribute("tabindex", isActive ? "0" : "-1"));
      var img = link.querySelector("img");
      (img && (img.setAttribute("alt", ""), img.setAttribute("aria-hidden", "true")),
        link.addEventListener("keydown", function (e) {
          var links = Array.from(scroller.querySelectorAll("[data-product-thumb]")),
            idx = links.indexOf(this);
          if (e.key === "ArrowRight" || e.key === "ArrowDown") {
            e.preventDefault();
            var next = links[(idx + 1) % links.length];
            (next.focus(), next.click());
          } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
            e.preventDefault();
            var prev = links[(idx - 1 + links.length) % links.length];
            (prev.focus(), prev.click());
          } else
            e.key === "Home"
              ? (e.preventDefault(), links[0].focus(), links[0].click())
              : e.key === "End" &&
                (e.preventDefault(),
                links[links.length - 1].focus(),
                links[links.length - 1].click());
        }));
    });
    var thumbObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (
          m.type === "attributes" &&
          m.attributeName === "class" &&
          m.target.hasAttribute("data-product-thumb")
        ) {
          var link = m.target,
            active = link.classList.contains("is-active");
          (link.setAttribute("aria-selected", active ? "true" : "false"),
            link.setAttribute("tabindex", active ? "0" : "-1"),
            active && announceImage(link));
        }
      });
    });
    if (
      (thumbLinks.forEach(function (link) {
        thumbObserver.observe(link, { attributes: !0, attributeFilter: ["class"] });
      }),
      !document.querySelector(".product__gallery-announce"))
    ) {
      var live = document.createElement("div");
      ((live.className = "product__gallery-announce"),
        live.setAttribute("aria-live", "polite"),
        live.setAttribute("aria-atomic", "true"),
        (live.style.cssText =
          "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;"),
        document.body.appendChild(live));
    }
  }
  /* Writes "Showing <image label>" into a hidden aria-live region so screen readers
   * announce the newly selected gallery image. */
  function announceImage(activeLink) {
    var live = document.querySelector(".product__gallery-announce");
    if (live) {
      var label = activeLink.getAttribute("aria-label") || "";
      ((live.textContent = ""),
        setTimeout(function () {
          live.textContent = "Showing " + label;
        }, 50));
    }
  }
  /* Elements with aria-hidden="true" must not contain tabbable controls (WCAG 4.1.2 / 2.4.3).
   * Sets tabindex=-1 on focusable children of aria-hidden containers (original value saved in
   * data-a11y-restore) and restores it when aria-hidden is removed. A MutationObserver keeps
   * this in sync for drawers/modals that toggle aria-hidden. */
  function initAriaHiddenFix() {
    function hideChildren(container) {
      container.querySelectorAll(FOCUSABLE).forEach(function (el) {
        if (!el.hasAttribute("data-a11y-restore")) {
          var original = el.getAttribute("tabindex");
          (el.setAttribute("data-a11y-restore", original !== null ? original : ""),
            el.setAttribute("tabindex", "-1"));
        }
      });
    }
    function restoreChildren(container) {
      container.querySelectorAll("[data-a11y-restore]").forEach(function (el) {
        var original = el.getAttribute("data-a11y-restore");
        (original === "" ? el.removeAttribute("tabindex") : el.setAttribute("tabindex", original),
          el.removeAttribute("data-a11y-restore"));
      });
    }
    (document.querySelectorAll('[aria-hidden="true"]').forEach(hideChildren),
      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.type !== "attributes" ||
            m.attributeName !== "aria-hidden" ||
            (m.target.getAttribute("aria-hidden") === "true"
              ? hideChildren(m.target)
              : restoreChildren(m.target));
        });
      }).observe(document.body, { attributes: !0, attributeFilter: ["aria-hidden"], subtree: !0 }));
  }
  /* Home page only: keep exactly one <h1>. Extra <h1>s (e.g. from slideshow/hero sections)
   * are converted to <h2> with attributes and content preserved (WCAG 1.3.1). */
  function initHomepageH1Fix() {
    if (document.body.classList.contains("template-index")) {
      var h1s = document.querySelectorAll("h1");
      h1s.length <= 1 ||
        Array.prototype.slice.call(h1s, 1).forEach(function (h1) {
          var h2 = document.createElement("h2");
          (Array.prototype.forEach.call(h1.attributes, function (attr) {
            h2.setAttribute(attr.name, attr.value);
          }),
            (h2.innerHTML = h1.innerHTML),
            h1.parentNode.replaceChild(h2, h1));
        });
    }
  }
  /* Generic "page" templates with no <h1>: inject a visually-hidden <h1> built from the
   * document title (text after "|" or before " - " / dash) so every page has a main heading. */
  function initMissingH1Fix() {
    if (document.body.classList.contains("template-page") && !document.querySelector("h1")) {
      var titleText = document.title || "",
        pageName = "";
      if (titleText.indexOf("|") !== -1) {
        var pipeParts = titleText.split("|");
        pageName = pipeParts[pipeParts.length - 1].trim();
      } else
        titleText.indexOf("\u2013") !== -1 || titleText.indexOf("\u2014") !== -1
          ? (pageName = titleText.split(/\s*[–—]\s*/)[0].trim())
          : titleText.indexOf(" - ") !== -1
            ? (pageName = titleText.split(" - ")[0].trim())
            : (pageName = titleText.trim());
      if (pageName) {
        var main =
          document.querySelector("main") ||
          document.querySelector("#MainContent") ||
          document.querySelector('[role="main"]');
        if (main) {
          var h1 = document.createElement("h1");
          ((h1.textContent = pageName),
            (h1.style.cssText =
              "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;"),
            main.insertBefore(h1, main.firstChild));
        }
      }
    }
  }
  /* (a) Marks decorative icon SVGs aria-hidden + focusable=false so they aren't announced.
   * (b) Adds aria-labels to icon-only buttons (lightbox arrows/close, tooltip close, modal close)
   *     when they have no accessible name (WCAG 4.1.2). */
  function initIconTitleFix() {
    document.querySelectorAll("svg").forEach(function (svg) {
      var svgClass = svg.className && svg.className.baseVal ? svg.className.baseVal : "",
        title = svg.querySelector("title"),
        titleText = title ? title.textContent.trim() : "",
        isIconSvg =
          /\bicon\b/i.test(svgClass) ||
          /^icon/i.test(titleText) ||
          svg.getAttribute("role") === "presentation";
      isIconSvg &&
        (svg.setAttribute("aria-hidden", "true"), svg.setAttribute("focusable", "false"));
    });
    var iconOnlyLabels = [
      [".pswp__button--close", "Close image viewer"],
      [".pswp__button--arrow--left", "Previous image"],
      [".pswp__button--arrow--right", "Next image"],
      [".pswp__button.btn--large", "Close image viewer"],
      [".pswp__button:not(.btn--large)", null],
      [".tool-tip__close", "Close"],
      [".modal__close", "Close dialog"],
      [".drawer__close-button", null],
    ];
    iconOnlyLabels.forEach(function (pair) {
      var selector = pair[0],
        label = pair[1];
      label &&
        document.querySelectorAll(selector).forEach(function (el) {
          var hasLabel = el.getAttribute("aria-label") || el.getAttribute("aria-labelledby"),
            visibleText = el.textContent.replace(/icon-[\w-]+/gi, "").trim();
          !hasLabel && !visibleText && el.setAttribute("aria-label", label);
        });
    });
  }
  /* Restores a clearly visible keyboard focus ring sitewide (WCAG 2.4.7):
   * 3px dark-red outline on :focus-visible (keyboard only, so mouse clicks stay clean).
   * Uses !important because the theme CSS removes outlines. */
  function initFocusFix() {
    var style = document.createElement("style");
    ((style.textContent = [
      "/* ADA fix Issue #10 \u2014 restore keyboard focus indicators sitewide */",
      "*:focus-visible {",
      "  outline: 3px solid #c00000 !important;",
      "  outline-offset: 3px !important;",
      "  box-shadow: none !important;",
      "}",
      "input:focus-visible,",
      "textarea:focus-visible,",
      "select:focus-visible {",
      "  outline: 3px solid #c00000 !important;",
      "  outline-offset: 0px !important;",
      "}",
    ].join("\n")),
      document.head.appendChild(style));
  }
  /* Images with no src and no alt attribute are treated as decorative: alt="" + aria-hidden
   * (WCAG 1.1.1). Real product images get alt text from fixProductGridAltText() below. */
  function initProductPageFix() {
    document.querySelectorAll("img").forEach(function (img) {
      var src = img.getAttribute("src"),
        alt = img.getAttribute("alt");
      (src === null || src === "") &&
        alt === null &&
        (img.setAttribute("alt", ""), img.setAttribute("aria-hidden", "true"));
    });
  }
  /* Gives logo links an accessible name: header logo -> "Chooka - Home";
   * footer/logo-bar links -> the image alt, or the link's hostname as a fallback (WCAG 2.4.4). */
  function initLogoLinkFix() {
    var logoLink = document.querySelector("a.logo__link");
    (logoLink &&
      !logoLink.getAttribute("aria-label") &&
      !logoLink.getAttribute("aria-labelledby") &&
      logoLink.setAttribute("aria-label", "Chooka \u2013 Home"),
      document.querySelectorAll("a.logo-bar__link, .logo-bar a[href]").forEach(function (link) {
        if (!(link.getAttribute("aria-label") || link.getAttribute("aria-labelledby"))) {
          var img = link.querySelector("img"),
            label = img && img.getAttribute("alt") ? img.getAttribute("alt") : null;
          if (!label && link.href)
            try {
              label = new URL(link.href).hostname.replace(/^www\./, "");
            } catch (e) {
              label = null;
            }
          label && link.setAttribute("aria-label", label);
        }
      }));
  }
  /* Blog grid placeholder links (href="#") are dead links: hide from AT and remove from the
   * tab order so keyboard users don't land on a link that does nothing. */
  function initBlogPlaceholderFix() {
    document.querySelectorAll('a.article__grid-image[href="#"]').forEach(function (link) {
      (link.setAttribute("aria-hidden", "true"), link.setAttribute("tabindex", "-1"));
    });
  }
})(),
  /* ===== (2) VerifyPass iframe title =====
   * The third-party VerifyPass discount widget injects an iframe with no title (WCAG 4.1.2).
   * Watch the DOM and set a title as soon as the iframe (#vfyps_iframe_html) appears. */
  document.addEventListener("DOMContentLoaded", function () {
    var observer = new MutationObserver(function () {
      var vfIframe = document.getElementById("vfyps_iframe_html");
      vfIframe && !vfIframe.title && (vfIframe.title = "Exclusive Discounts Verification");
    });
    observer.observe(document.body, { childList: !0, subtree: !0 });
  }),
  /* ===== (3) Product grid alt text =====
   * Collection/grid images are sometimes rendered with alt="". Fill the alt from the product
   * name in the same card (WCAG 1.1.1). Re-runs (debounced 150ms) when new cards load via
   * infinite scroll / filtering. Exposed as window.shopchookaFixAltText() for manual calls. */
  (function () {
    "use strict";
    /* Walks up to 8 parents from an <img> to find the card's product link and returns the
     * product name (link text before the "$" price), or null if none found. */
    function productNameFromCard(img) {
      for (var el = img, i = 0; i < 8 && el; i++) {
        if (el !== img && el.querySelectorAll) {
          var link =
            el.querySelector("a.grid-product__link") || el.querySelector('a[href*="/products/"]');
          if (link && link.textContent && link.textContent.trim()) {
            var raw = link.textContent.trim(),
              name = raw.split("$")[0].trim();
            if (name) return name;
          }
        }
        el = el.parentElement;
      }
      return null;
    }
    /* Sets alt = product name on every img[alt=""] inside root (default: document).
     * Returns how many images were fixed. */
    function fixProductGridAltText(root) {
      var scope = root || document,
        images = scope.querySelectorAll('img[alt=""]'),
        fixedCount = 0;
      return (
        images.forEach(function (img) {
          var name = productNameFromCard(img);
          name && (img.setAttribute("alt", name), fixedCount++);
        }),
        fixedCount
      );
    }
    function init() {
      fixProductGridAltText(document);
    }
    document.readyState === "loading"
      ? document.addEventListener("DOMContentLoaded", init)
      : init();
    var debounceTimer = null,
      observer = new MutationObserver(function (mutations) {
        var relevant = mutations.some(function (m) {
          return m.addedNodes && m.addedNodes.length > 0;
        });
        relevant &&
          (clearTimeout(debounceTimer),
          (debounceTimer = setTimeout(function () {
            fixProductGridAltText(document);
          }, 150)));
      });
    (observer.observe(document.body, { childList: !0, subtree: !0 }),
      (window.shopchookaFixAltText = fixProductGridAltText));
  })(),
  /* ===== (4) Cookie banner label =====
   * The Shopify privacy banner (#shopify-pc__banner) has no accessible name. Label it
   * "Cookie consent" as soon as it is injected, then stop observing. */
  (function () {
    var observer = new MutationObserver(function () {
      var banner = document.getElementById("shopify-pc__banner");
      banner &&
        !banner.hasAttribute("aria-label") &&
        (banner.setAttribute("aria-label", "Cookie consent"), observer.disconnect());
    });
    observer.observe(document.documentElement, { childList: !0, subtree: !0 });
  })());
/* ===== (5) Form success headings =====
 * Globo form success messages (h4) and the privacy opt-out form (h3) skip heading levels.
 * Promote them to <h2> (attributes/content preserved) now and whenever the DOM changes
 * (WCAG 1.3.1). */
function initFormSuccessHeadingFix() {
  /* Replaces a heading element with an <h2> copy (same attributes and content). */
  function promoteToH2(el) {
    if (el.tagName !== "H2") {
      var h2 = document.createElement("h2");
      (Array.prototype.forEach.call(el.attributes, function (attr) {
        h2.setAttribute(attr.name, attr.value);
      }),
        (h2.innerHTML = el.innerHTML),
        el.parentNode.replaceChild(h2, el));
    }
  }
  function fix() {
    (document.querySelectorAll(".globo-form-app h4").forEach(promoteToH2),
      document.querySelectorAll(".pc--opt-out-form h3").forEach(promoteToH2));
  }
  (fix(), new MutationObserver(fix).observe(document.body, { childList: !0, subtree: !0 }));
}
initFormSuccessHeadingFix();
//# sourceMappingURL=/cdn/shop/t/85/assets/ada-shopchooka.js.map?v=3386114246299361791788821664
/* ===== (6) Minor fixes (added Oct 2026) =====
 * run() is idempotent: it is called on load and again (debounced 200ms) on any DOM change,
 * so dynamically added elements (sticky filter bar clone, quick-view, AJAX filtering) are
 * covered too. Previous skip-link injector (initSkipNav) was removed: the theme's own
 * .skip-link ("Skip to content") in theme.liquid is the single skip link. */
(function () {
  function run() {
    /* Fix 1 - aria-controls="...dupe" (WCAG 4.1.2).
     * main.js clones the collection utility bar for sticky mobile and suffixes its IDs with
     * "dupe" (theme.suffixIds), so the clone's Filter button ends up controlling
     * "CollectionFilterFormdupe", which doesn't exist. Re-point it to the real form
     * (CollectionFilterForm), or remove it if that id isn't on the page. */
    document.querySelectorAll('[aria-controls$="dupe"]').forEach(function (el) {
      var id = el.getAttribute("aria-controls").replace(/dupe$/, "");
      if (document.getElementById(id)) el.setAttribute("aria-controls", id);
      else el.removeAttribute("aria-controls");
    });
    /* Fix 2 - skip-link targets (WCAG 2.4.1).
     * Target of any a.skip-link (e.g. "Skip to product information") gets tabindex="-1"
     * so keyboard focus actually moves there when the link is activated. */
    document.querySelectorAll('a.skip-link[href^="#"]').forEach(function (a) {
      var t =
        a.getAttribute("href").length > 1 &&
        document.getElementById(a.getAttribute("href").slice(1));
      if (t && !t.hasAttribute("tabindex")) t.setAttribute("tabindex", "-1");
    });
    /* Fix 3 - Space key on <toggle-target role="button"> (WCAG 2.1.1).
     * Collection filter group headers are custom elements; main.js only opens them on Enter
     * (keyup). Native buttons also activate on Space, so add that: block page scroll on
     * keydown, click on keyup. data-a11y-space prevents double-binding. */
    document.querySelectorAll('toggle-target[role="button"]').forEach(function (el) {
      if (el.dataset.a11ySpace) return;
      el.dataset.a11ySpace = "1";
      el.addEventListener("keydown", function (e) {
        if (e.key === " " || e.key === "Spacebar") e.preventDefault();
      });
      el.addEventListener("keyup", function (e) {
        if (e.key === " " || e.key === "Spacebar") {
          e.preventDefault();
          el.click();
        }
      });
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run);
  else run();
  var t = null;
  new MutationObserver(function () {
    clearTimeout(t);
    t = setTimeout(run, 200);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();